import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, Home, Heart, Pill, FileText, Calendar,
  Users, QrCode, Settings, LogOut, Globe, Volume2, VolumeX, Menu, Bell, Leaf, AlertTriangle, ChevronLeft
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useState } from 'react';
import AayuAssistant from './AayuAssistant';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage, speak, stopSpeaking } = useLanguage();
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { icon: Home, label: t('dashboard'), path: '/dashboard' },
    { icon: AlertTriangle, label: language === 'hi' ? 'आपातकालीन कार्ड' : 'Emergency Card', path: '/emergency-profile', highlight: true },
    { icon: Heart, label: t('medicalHistory'), path: '/medical-history' },
    { icon: Pill, label: t('prescriptions'), path: '/prescriptions' },
    { icon: Leaf, label: language === 'hi' ? 'सप्लीमेंट्स' : 'Supplements', path: '/supplements' },
    { icon: FileText, label: t('reports'), path: '/reports' },
    { icon: Calendar, label: t('diary'), path: '/diary' },
    { icon: Bell, label: t('reminders'), path: '/reminders' },
    { icon: Users, label: t('referrals'), path: '/referrals' },
    { icon: QrCode, label: t('doctorAccess'), path: '/doctor-access' },
  ];

  const bottomNavItems = [
    { icon: Home, label: t('dashboard'), path: '/dashboard' },
    { icon: Pill, label: t('prescriptions'), path: '/prescriptions' },
    { icon: Bell, label: t('reminders'), path: '/reminders' },
    { icon: Calendar, label: t('diary'), path: '/diary' },
    { icon: Settings, label: t('settings'), path: '/settings' },
  ];

  const handleSpeak = () => {
    if (isSpeaking) {
      stopSpeaking();
      setIsSpeaking(false);
    } else {
      speak(t('appTagline'));
      setIsSpeaking(true);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FDFCF8] w-full" style={{ width: '100vw', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Desktop Header */}
      <header className="secure-header text-white sticky top-0 z-40 w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="flex items-center justify-between">
            {/* Back Button + Logo */}
            <div className="flex items-center gap-2">
              {/* Back Button - show on all pages except dashboard */}
              {location.pathname !== '/dashboard' && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/20 rounded-full"
                  data-testid="back-button"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
              )}
              
              <Link to="/dashboard" className="flex items-center gap-3" data-testid="logo-link">
                <ShieldCheck className="h-8 w-8" />
                <div>
                  <h1 className="text-xl font-bold font-['Manrope']">{t('appName')}</h1>
                  <p className="text-xs opacity-80 hidden sm:block">{t('appTagline')}</p>
                </div>
              </Link>
            </div>

            {/* Desktop Nav - only show on xl screens (1280px+), limit items shown */}
            <nav className="hidden xl:flex items-center gap-1 overflow-hidden max-w-[600px]">
              {navItems.slice(0, 6).map((item) => (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    className={`text-white hover:bg-white/20 text-sm px-3 ${
                      isActive(item.path) ? 'bg-white/20' : ''
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <item.icon className="h-4 w-4 mr-1" />
                    <span className="truncate">{item.label}</span>
                  </Button>
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSpeak}
                className="text-white hover:bg-white/20"
                data-testid="header-speak-btn"
              >
                {isSpeaking ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
              </Button>
              
              <Button
                variant="ghost"
                onClick={toggleLanguage}
                className="text-white hover:bg-white/20 lang-toggle hidden sm:flex"
                data-testid="header-language-toggle"
              >
                <Globe className="h-5 w-5 mr-2" />
                {language === 'en' ? 'हिंदी' : 'EN'}
              </Button>

              {/* Settings Button - visible on tablet and desktop */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/settings')}
                className="text-white hover:bg-white/20"
                data-testid="header-settings-btn"
              >
                <Settings className="h-5 w-5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:bg-white/20" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-[#FFB300] flex items-center justify-center text-slate-900 font-bold">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="font-semibold">{user?.name}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('settings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/20" data-testid="mobile-menu-btn">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72">
                  <nav className="flex flex-col gap-2 mt-8">
                    {navItems.map((item) => (
                      <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                        <Button
                          variant={isActive(item.path) ? 'default' : 'ghost'}
                          className={`w-full justify-start ${
                            isActive(item.path) ? 'bg-[#0D2340]' : ''
                          } ${item.highlight ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' : ''}`}
                        >
                          <item.icon className={`h-5 w-5 mr-3 ${item.highlight ? 'text-red-600' : ''}`} />
                          {item.label}
                        </Button>
                      </Link>
                    ))}
                    <hr className="my-4" />
                    <Button variant="ghost" onClick={toggleLanguage} className="justify-start">
                      <Globe className="h-5 w-5 mr-3" />
                      {language === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
                    </Button>
                    <Button variant="ghost" onClick={handleLogout} className="justify-start text-red-600">
                      <LogOut className="h-5 w-5 mr-3" />
                      {t('logout')}
                    </Button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-6 page-enter" style={{ width: '100%', maxWidth: '100%' }}>
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav nav-shadow lg:hidden" data-testid="bottom-nav">
        <div className="flex justify-around items-center">
          {bottomNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className={`flex-col h-16 px-3 relative transition-all ${
                    active 
                      ? 'text-[#0D2340] bg-[#0D2340]/10' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`bottom-nav-${item.path.replace('/', '')}`}
                >
                  {/* Active indicator dot */}
                  {active && (
                    <span className="absolute top-1 w-1.5 h-1.5 bg-[#0D2340] rounded-full" />
                  )}
                  <item.icon className={`h-5 w-5 ${active ? 'text-[#0D2340]' : ''}`} />
                  <span className={`text-[10px] mt-1 font-medium ${active ? 'text-[#0D2340]' : ''}`}>
                    {item.label}
                  </span>
                </Button>
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* Aayu Assistant - Available for all subscribers */}
      <AayuAssistant />
    </div>
  );
};

export default Layout;
